import { FC, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { TextInput } from 'components/ui';

import { interceptionRelevanceOptions } from '../../constants/interception';
import { initialInterceptionQuickFilterSettings } from '../../constants/localStorage';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  InterceptionMember,
  InterceptionNetwork,
} from '../../interfaces/interceptions';
import {
  getInterceptionMembersThunk,
  getInterceptionNetworksThunk,
} from '../../store/slices/interception/actions';
import {
  interceptionDepartmentsSelector,
  interceptionMembersSelector,
  interceptionNetworksSelector,
} from '../../store/slices/interception/selectors';
import { useLayerQuickFilterLocalStorage } from '../../utils/localStorage';
import { DateFilterSelector } from '../Map/Tabs/Layers/QuickFilters/DateFilterSelector';
import { CreatedAtFilter } from '../Map/Tabs/Layers/types';

import InterceptionsMemberCard from './InterceptionsMemberCard';
import InterceptionsNetworkCard from './InterceptionsNetworkCard';

export interface InterceptionsFilterSidebarProps {
  openInterceptionMember: (interceptionMember: InterceptionMember) => void;
  openInterceptionNetwork: (interceptionNetwork: InterceptionNetwork) => void;
  selectedNetworks: InterceptionNetwork[];
  selectedMembers: InterceptionMember[];
  selectNetwork: (interceptionNetwork: InterceptionNetwork) => void;
  selectMember: (interceptionMember: InterceptionMember) => void;
  openedNetwork: InterceptionNetwork | null;
  openedMember: InterceptionMember | null;
}

const InterceptionsFilterSidebar: FC<InterceptionsFilterSidebarProps> = ({
  openInterceptionMember,
  openInterceptionNetwork,
  selectedNetworks,
  selectedMembers,
  selectNetwork,
  selectMember,
  openedMember,
  openedNetwork,
}) => {
  const [showNetworksTab, setShowNetworksTab] = useState(true);
  const [searchString, setSearchString] = useState('');

  const [quickFilterSettings, setQuickFilterSettings] =
    useLayerQuickFilterLocalStorage(initialInterceptionQuickFilterSettings);

  const onCreatedAtFilterChange = (createdAtFilter?: CreatedAtFilter) => {};

  const networks = useAppSelector(interceptionNetworksSelector);
  const members = useAppSelector(interceptionMembersSelector);
  const departments = useAppSelector(interceptionDepartmentsSelector);
  const dispatch = useAppDispatch();

  const filteredNetworks = useMemo(() => {
    return networks.filter(
      (network) =>
        (network.name &&
          network.name.toLowerCase().includes(searchString.toLowerCase())) ||
        network.frequency
          .toString()
          .toLowerCase()
          .includes(searchString.toLowerCase()) ||
        departments.find((dep) =>
          dep.name.toLowerCase().includes(searchString.toLowerCase())
        )
    );
  }, [networks, searchString, departments]);

  const filteredMembers = useMemo(() => {
    return members.filter(
      (member) =>
        (member.name &&
          member.name.toLowerCase().includes(searchString.toLowerCase())) ||
        member.id
          .toString()
          .toLowerCase()
          .includes(searchString.toLowerCase()) ||
        departments.find((dep) =>
          dep.name.toLowerCase().includes(searchString.toLowerCase())
        )
    );
  }, [members, searchString, departments]);

  useEffect(() => {
    if (quickFilterSettings.startDate && quickFilterSettings.endDate) {
      dispatch(
        getInterceptionNetworksThunk({
          startDate: quickFilterSettings.startDate,
          endDate: quickFilterSettings.endDate,
        })
      );
      dispatch(
        getInterceptionMembersThunk({
          startDate: quickFilterSettings.startDate,
          endDate: quickFilterSettings.endDate,
        })
      );
    }
  }, [quickFilterSettings]);

  return (
    <div className="absolute w-[450px] !h-[calc(100vh-96px)] bg-dark rounded-[10px] overflow-auto ml-1.5">
      <div className="sticky top-0 z-[1] bg-dark">
        <div className="header__links">
          <div className="flex flex-row w-full">
            <div
              className={cn(
                'flex gap-1 items-center w-full justify-center',
                {
                  'header-link__active': showNetworksTab,
                  'bg-light': !showNetworksTab,
                },
                'py-2 px-6'
              )}
              onClick={() => setShowNetworksTab(true)}
            >
              <p className="header-link__title tpg-b1">Радиосети</p>
            </div>
            <div
              className={cn(
                'flex gap-1 items-center w-full justify-center',
                {
                  'header-link__active': !showNetworksTab,
                  'bg-light': showNetworksTab,
                },
                'py-2 px-6'
              )}
              onClick={() => setShowNetworksTab(false)}
            >
              <p className="header-link__title tpg-b1">Абоненты</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between w-full px-4 py-2">
          <div className="w-full h-13 pt-4 flex flex-row gap-1 tpg-c2 text-tpg_base">
            <DateFilterSelector
              filterSettings={quickFilterSettings}
              setFilterSettings={setQuickFilterSettings}
              onCreatedAtFilterChange={onCreatedAtFilterChange}
              options={interceptionRelevanceOptions}
              showDatePicker={false}
            />
          </div>
          {/*<div className="text-[11px] tpg-c2">*/}
          {/*  Сортировка <span className="text-bright_product">по времени</span>*/}
          {/*</div>*/}
        </div>
        <div className="px-4 pb-2">
          <TextInput
            placeholder={`Поиск по ${
              showNetworksTab ? 'радиосети' : 'абоненту'
            }`}
            searchIcon
            onChange={(value) => setSearchString(value)}
            value={searchString}
          ></TextInput>
        </div>
      </div>
      <div className="flex flex-col">
        {showNetworksTab
          ? filteredNetworks.map((interceptionNetwork, index) => (
              <div
                key={interceptionNetwork.id}
                onClick={() => openInterceptionNetwork(interceptionNetwork)}
              >
                <InterceptionsNetworkCard
                  network={interceptionNetwork}
                  evenIndex={index % 2 === 0}
                  selectedNetworks={selectedNetworks}
                  selectNetwork={selectNetwork}
                  isOpened={openedNetwork?.id === interceptionNetwork.id}
                />
              </div>
            ))
          : filteredMembers.map((interceptionMember, index) => (
              <div
                key={interceptionMember.id}
                onClick={() => openInterceptionMember(interceptionMember)}
              >
                <InterceptionsMemberCard
                  member={interceptionMember}
                  evenIndex={index % 2 === 0}
                  selectedMembers={selectedMembers}
                  selectMember={selectMember}
                  isOpened={openedMember?.id === interceptionMember.id}
                />
              </div>
            ))}
      </div>
    </div>
  );
};

export default InterceptionsFilterSidebar;
